import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useAuthStore } from './store/auth';
import { api } from './api/client';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import AddDomain from './pages/AddDomain';
import Admin from './pages/Admin';
import Reseller from './pages/Reseller';
import { RESELLER_CONSOLE_ENABLED } from './lib/uiFlags';
import Report from './pages/Report';
import Invoice from './pages/Invoice';

function RequireAuth({ children }) {
  const token = useAuthStore((s) => s.token);
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

// Re-fetches the current user once on mount so stale localStorage entries
// (e.g. created before `role` existed) pick up fresh fields.
function useRefreshUser() {
  const { token, setUser } = useAuthStore();
  useEffect(() => {
    if (!token) return;
    api
      .get('/api/auth/me')
      .then((r) => setUser(r.data.user))
      .catch(() => {});
  }, [token, setUser]);
}

export default function App() {
  useRefreshUser();
  return (
    <>
      {/* Global toast container. Branded to match the rest of the app —
          rounded corners, brand-aware accent strip, and bottom-right
          placement so it doesn't cover dashboard heros / forms. */}
      <Toaster
        position="bottom-right"
        richColors
        closeButton
        toastOptions={{
          className: 'font-sans',
          style: {
            borderRadius: '12px',
            fontSize: '13.5px',
          },
        }}
      />
      <RoutesTree />
    </>
  );
}

function RoutesTree() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/r/:slug" element={<Report mode="public" />} />
      <Route
        path="/report/:id"
        element={
          <RequireAuth>
            <Report mode="authed" />
          </RequireAuth>
        }
      />
      <Route
        path="/invoices/:id"
        element={
          <RequireAuth>
            <Invoice />
          </RequireAuth>
        }
      />
      <Route
        path="/domains/new"
        element={
          <RequireAuth>
            <AddDomain />
          </RequireAuth>
        }
      />
      <Route
        path="/admin/*"
        element={
          <RequireAuth>
            <Admin />
          </RequireAuth>
        }
      />
      <Route
        path="/reseller/*"
        element={
          RESELLER_CONSOLE_ENABLED ? (
            <RequireAuth>
              <Reseller />
            </RequireAuth>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <Dashboard />
          </RequireAuth>
        }
      />
    </Routes>
  );
}

